/**
 * 场景集合树共享工具：把选中的叶子节点 id 集合渲染成一颗只保留「选中路径」的
 * Ant Design `Tree` 组件数据，并提供叶子节点计数。原先在 pages/JobList 里就地
 * 定义、pages/JobTaskList 的 compareMode 分支又抄了一份，抽到 common 消除重复。
 */
import type { SceneTreeNode } from '@/apis/simulation';

/** Ant Design Tree 节点数据结构（本工具输出的 shape），只写用得到的字段。 */
export interface DisplayTreeNode {
  title: string;
  key: number | string;
  isLeaf: boolean;
  children?: DisplayTreeNode[];
  className?: string;
}

/** 在场景树中按 targetId 定位节点，返回从根到该节点的名字路径（不到返回 null）。 */
export const findScenePath = (
  nodes: SceneTreeNode[],
  targetId: number,
  path: string[] = [],
): string[] | null => {
  for (const node of nodes) {
    const currentPath = [...path, node.name];
    if (node.id === targetId) {
      return currentPath;
    }
    if (node.children && node.children.length > 0) {
      const found = findScenePath(node.children, targetId, currentPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/** 在场景树中按 targetId 定位节点，返回节点本身（不到返回 null）。 */
export const findSceneNode = (
  nodes: SceneTreeNode[],
  targetId: number,
): SceneTreeNode | null => {
  for (const node of nodes) {
    if (node.id === targetId) {
      return node;
    }
    if (node.children && node.children.length > 0) {
      const found = findSceneNode(node.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/** node 自身或其后代是否命中 selectedIds —— 剪枝辅助。 */
export const hasSelectedDescendant = (
  node: SceneTreeNode,
  selectedIds: number[],
): boolean => {
  if (selectedIds.includes(node.id)) {
    return true;
  }
  if (node.children && node.children.length > 0) {
    return node.children.some((child) => hasSelectedDescendant(child, selectedIds));
  }
  return false;
};

/** 单节点递归转换（内部使用）：处理多层嵌套的显示裁剪。 */
export const convertNodeToTreeData = (
  node: SceneTreeNode,
  selectedIds: number[],
): DisplayTreeNode => {
  const isSelected = selectedIds.includes(node.id);
  let children: DisplayTreeNode[] | undefined;
  if (node.children && node.children.length > 0) {
    if (isSelected && node.nodeFlag === 0) {
      children = node.children.map((child) => convertNodeToTreeData(child, selectedIds));
    } else {
      const filteredChildren = node.children
        .filter((child) => hasSelectedDescendant(child, selectedIds))
        .map((child) => convertNodeToTreeData(child, selectedIds));
      children = filteredChildren.length > 0 ? filteredChildren : undefined;
    }
  }
  return {
    title: node.name,
    key: node.id,
    isLeaf: node.nodeFlag === 1,
    children,
    ...(isSelected && { className: 'selected-scene-node' }),
  };
};

/** 把场景树裁剪成「只保留选中节点所在路径」的 Ant Design Tree 数据。 */
export const convertSceneTreeToTreeForDisplay = (
  nodes: SceneTreeNode[],
  selectedIds: number[],
): DisplayTreeNode[] => {
  return nodes
    .filter((node) => hasSelectedDescendant(node, selectedIds))
    .map((node) => convertNodeToTreeData(node, selectedIds));
};

/** 统计裁剪后 treeData 中的叶子节点数。 */
export const countLeafNodes = (treeData: DisplayTreeNode[]): number => {
  let count = 0;
  for (const node of treeData) {
    if (node.isLeaf === true) {
      count++;
    }
    if (node.children && node.children.length > 0) {
      count += countLeafNodes(node.children);
    }
  }
  return count;
};
